const CREDENTIALS = [
  {
    status: "Current",
    title: "Enrolled Agent (EA)",
    body: "Federally licensed by the U.S. Department of the Treasury. Authority to represent taxpayers before all administrative levels of the Internal Revenue Service — examination, collection, and appeals — in any state.",
  },
  {
    status: "Current",
    title: "Project Management Professional (PMP)",
    body: "Credentialed by the Project Management Institute. Reflects formal training and experience in scope, schedule, cost, risk, and stakeholder management — applied here to multi-year tax engagements and federal-agency advisory work.",
  },
  {
    status: "Current",
    title: "Master of Business Administration (MBA)",
    body: "Graduate degree in business administration from New York University, Stern Graduate School of Business — see Education section above.",
  },
];

export default function TeamPage() {
  return (
    <div data-testid="page-team" className="bg-[#F9F8F6]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— The Practice</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#0F292F] font-light tracking-tight max-w-4xl leading-[1.05]">
          A <span className="italic text-[#8E6A2B]">deliberately boutique</span> practice.
        </h1>
        <p className="mt-8 text-lg text-[#52525B] max-w-2xl leading-relaxed">
          H. Elliot Associates is structured by design as a principal-led firm. Every client
          is served directly by the founder — never handed off to a junior preparer or
          rotated through an associate pool. The practice is built around a small, considered
          roster of clients and a deep professional network for the work that lies beyond
          tax and retirement planning.
        </p>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="aspect-square w-full max-w-[200px] overflow-hidden bg-[#0F292F] ring-1 ring-black/10">
            <img
              src="/images/harold-hodges.jpg"
              alt="Harold Hodges, MBA · EA · PMP — founder of H. Elliot Associates"
              className="w-full h-full object-cover scale-110"
              style={{ filter: "grayscale(60%) contrast(1.05) brightness(0.97)" }}
            />
          </div>
          <div className="mt-4 w-12 h-px bg-[#8E6A2B]" />
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#8E6A2B]">Harold Hodges</div>
          <div className="text-xs text-[#52525B] mt-1">MBA · EA · PMP · Founder</div>
          <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[#52525B]">Rockville, Maryland · Practicing nationwide</div>
        </div>

        <div className="lg:col-span-8">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F292F] font-light leading-tight mb-6">
            About Harold Hodges
          </h2>
          <div className="space-y-5 text-[#1A1A1A] leading-relaxed">
            <p>
              Harold Hodges brings more than three decades of experience across the federal
              government and nonprofit sector — including over $1 billion in federal and
              state funds managed — to the practice of tax. Over twenty years, he served
              with the United States Department of State, where he worked on complex
              international economic, financial, and policy matters across multiple
              administrations and overseas postings — including financial-leadership tours
              at U.S. embassies in Panama, Algeria, Paraguay, and Côte d'Ivoire.
            </p>
            <p>
              That career — built on careful analysis, clear writing, and earning the trust
              of people whose decisions had real consequences — is the same discipline he
              now brings to his clients' returns. Tax work, at its best, is policy made
              personal: every line on a 1040 is the result of a rule someone wrote, and a
              good practitioner's job is to make the rules work for the taxpayer rather than
              against them.
            </p>
            <p>
              As a federally licensed Enrolled Agent, Harold is admitted to represent
              taxpayers in all fifty states and before all administrative levels of the
              Internal Revenue Service. He founded H. Elliot Associates to deliver
              year-round tax work with the clarity and care that high-fee practices reserve
              for their largest clients — to every client.
            </p>
            <p>
              Harold's commitment to clients is straightforward: deliver year-round work
              with the clarity and care typically reserved for the largest accounts of
              high-fee firms — and apply the same discipline to every return, every plan,
              and every conversation.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F3F1EC] border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Education</div>
            </div>
            <div className="lg:col-span-8">
              <ul className="divide-y divide-black/10">
                <li data-testid="edu-sais" className="py-8 grid md:grid-cols-12 gap-4 items-baseline">
                  <div className="md:col-span-4 text-xs uppercase tracking-[0.2em] text-[#8E6A2B]">
                    Johns Hopkins SAIS
                  </div>
                  <div className="md:col-span-8">
                    <div className="font-serif text-2xl md:text-3xl text-[#0F292F]">
                      M.A., International Economics
                    </div>
                    <div className="text-sm text-[#52525B] mt-2">
                      The Paul H. Nitze School of Advanced International Studies
                    </div>
                  </div>
                </li>
                <li data-testid="edu-stern" className="py-8 grid md:grid-cols-12 gap-4 items-baseline">
                  <div className="md:col-span-4 text-xs uppercase tracking-[0.2em] text-[#8E6A2B]">
                    New York University
                  </div>
                  <div className="md:col-span-8">
                    <div className="font-serif text-2xl md:text-3xl text-[#0F292F]">
                      M.B.A.
                    </div>
                    <div className="text-sm text-[#52525B] mt-2">
                      Stern Graduate School of Business
                    </div>
                  </div>
                </li>
                <li data-testid="edu-dartmouth" className="py-8 grid md:grid-cols-12 gap-4 items-baseline">
                  <div className="md:col-span-4 text-xs uppercase tracking-[0.2em] text-[#8E6A2B]">
                    Dartmouth College
                  </div>
                  <div className="md:col-span-8">
                    <div className="font-serif text-2xl md:text-3xl text-[#0F292F]">
                      B.A., Economics
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Credentials</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-tight mb-12">
            Currently held by the principal.
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {CREDENTIALS.map((c, i) => (
              <div key={c.title} data-testid={`credential-${i}`} className="bg-white p-10 hover:bg-[#F3F1EC] transition-colors">
                <h3 className="font-serif text-2xl md:text-3xl text-[#0F292F] mb-4 leading-tight">{c.title}</h3>
                <p className="text-[#52525B] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F3F1EC] border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Strategic Partners & Network</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-tight mb-6">
            A small practice with a deep network.
          </h2>
          <p className="text-lg text-[#52525B] max-w-3xl leading-relaxed mb-12">
            We deliberately keep the practice small so every client is served by the principal.
            For the work that lies outside our two specialties, we coordinate with an established
            network of independent professionals — at no markup and with full transparency.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            <div className="bg-white p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E6A2B] mb-3">Coordination</div>
              <h3 className="font-serif text-xl text-[#0F292F] mb-3 leading-tight">Estate & Trust Counsel</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                Independent attorneys we coordinate with for wills, trusts, beneficiary review,
                and intergenerational transfers.
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E6A2B] mb-3">Coordination</div>
              <h3 className="font-serif text-xl text-[#0F292F] mb-3 leading-tight">Investment Professionals</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                Where clients need investment advice or portfolio management, we coordinate
                with vetted independent advisors — and stay focused on the tax side.
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E6A2B] mb-3">Coordination</div>
              <h3 className="font-serif text-xl text-[#0F292F] mb-3 leading-tight">Insurance Specialists</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                Independent brokers for life, long-term care, and FEHB-supplemental coverage
                when the planning calls for them.
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8E6A2B] mb-3">Coordination</div>
              <h3 className="font-serif text-xl text-[#0F292F] mb-3 leading-tight">Bookkeeping Partners</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                For small-business clients that need monthly bookkeeping, we refer to a
                short list of trusted independent bookkeepers.
              </p>
            </div>
          </div>

          <div className="mt-16">
            <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Professional Affiliations</div>
            <ul className="divide-y divide-black/10 max-w-3xl">
              <li data-testid="affil-aga" className="py-6 grid md:grid-cols-12 gap-4 items-baseline">
                <div className="md:col-span-4 text-xs uppercase tracking-[0.2em] text-[#52525B]">Member</div>
                <div className="md:col-span-8">
                  <div className="font-serif text-2xl md:text-3xl text-[#0F292F]">
                    Association of Government Accountants
                  </div>
                  <div className="text-sm text-[#52525B] mt-1">
                    The professional association for government finance professionals — federal, state, and local. <span className="text-[#8E6A2B]">AGA</span>
                  </div>
                </div>
              </li>
              <li data-testid="affil-fpa" className="py-6 grid md:grid-cols-12 gap-4 items-baseline">
                <div className="md:col-span-4 text-xs uppercase tracking-[0.2em] text-[#52525B]">Member</div>
                <div className="md:col-span-8">
                  <div className="font-serif text-2xl md:text-3xl text-[#0F292F]">
                    Financial Planning Association
                  </div>
                  <div className="text-sm text-[#52525B] mt-1">
                    The largest membership organization for CFP® professionals and the broader financial-planning community. <span className="text-[#8E6A2B]">FPA</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
